import React, { useState, useMemo } from "react";
import { Search, Plus, Pencil, BarChart3, Link2, ExternalLink, Link, ArchiveRestore } from "lucide-react";
import { Button, Input, DataTable, Spinner, Badge, Tooltip, confirmDialog } from "@/components/ui";
import { SmartLink } from "@/services/bedrock/dtos";
import { useDebounced, kFormatter, useSmartLinkSearch } from "@/utils";
import { SmartLinkDialog, Mode, SmartLinkExtraParamsDialog, SmartLinkStatsDialog } from "@/containers/Coach/SmartLink";
import { ColumnDef } from "@tanstack/react-table";
import { cn } from "@/lib/utils";

type StatusFilter = "ALL" | "ACTIVE" | "ARCHIVED";

export function SmartLinksTab() {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounced(searchTerm, 500);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("ACTIVE");
  const [pagination, setPagination] = useState({ page: 1, perPage: 25 });

  const [isDialogOpen, setIsSmartLinkDialogOpen] = useState(false);
  const [isStatsOpen, setIsStatsDialogOpen] = useState(false);
  const [isParamsOpen, setIsParamsDialogOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState<SmartLink | undefined>();
  const [dialogInitialMode, setDialogInitialMode] = useState<Mode | undefined>();

  const { data, isLoading, updateSmartLink } = useSmartLinkSearch({
    searchTerm: debouncedSearch,
    isActive: statusFilter === "ALL" ? undefined : statusFilter === "ACTIVE",
    extraFields: ["post_count", "total_click_count", "prompt_count"],
    page: pagination.page,
    perPage: pagination.perPage,
  });

  // Reset page when filters change
  React.useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [debouncedSearch, statusFilter]);

  const handleReactivate = async (link: SmartLink) => {
    const confirmed = await confirmDialog({
      title: "Re-activate Smart Link?",
      description: (
        <div className="flex flex-col gap-3">
          <p>
            Would you like to restore <strong>{link.name}</strong> to your active library?
          </p>
          <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-800 leading-relaxed font-sans text-left">
            <span className="font-bold block mb-1">Restoring Impact</span>
            Re-activating this link will restore its original tracking destination for{" "}
            <strong>all published posts</strong>, effectively "restoring" the redirects that were altered when it was
            archived.
          </div>
        </div>
      ),
      confirmLabel: "Re-activate",
      cancelLabel: "Cancel",
      size: "md",
    });

    if (confirmed) {
      try {
        await updateSmartLink({
          id: link.id,
          data: { isActive: true },
        });
      } catch (error) {
        // Error is handled by mutation toast
      }
    }
  };

  const columns = useMemo<ColumnDef<SmartLink>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Link Info",
        cell: ({ row }) => (
          <div className="flex items-start gap-3 py-1">
            <div className="size-10 rounded-md overflow-hidden bg-slate-100 border border-slate-200 shrink-0 flex items-center justify-center">
              {row.original.ogImageUrl ? (
                <img src={row.original.ogImageUrl} alt="" className="size-full object-cover" />
              ) : (
                <Link2 className="size-5 text-slate-300" />
              )}
            </div>
            <div className="flex flex-col min-w-0 max-w-md text-left">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="font-semibold text-slate-900 leading-tight">{row.original.name}</span>
                {!row.original.isActive && (
                  <Badge
                    variant="outline"
                    className="text-[10px] h-4 px-1.5 font-bold uppercase tracking-wider text-slate-400 border-slate-200 bg-slate-50"
                  >
                    Archived
                  </Badge>
                )}
              </div>
              <div className="flex items-start gap-1.5 group/url">
                <span className="text-xs text-muted-foreground break-all leading-normal">
                  {row.original.destinationUrl}
                </span>
                <a
                  href={row.original.destinationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="opacity-0 group-hover/url:opacity-100 transition-opacity shrink-0 mt-0.5"
                >
                  <ExternalLink className="size-3 text-primary" />
                </a>
              </div>
            </div>
          </div>
        ),
      },
      {
        accessorKey: "promptCount",
        header: "Prompts",
        cell: ({ row }) => <span className="font-medium">{row.original.promptCount || 0}</span>,
      },
      {
        accessorKey: "postCount",
        header: "Total Posts",
        cell: ({ row }) => <span className="font-medium">{row.original.postCount || 0}</span>,
      },
      {
        accessorKey: "totalClickCount",
        header: "Total Clicks",
        cell: ({ row }) => (
          <Badge
            variant="secondary"
            className="font-bold text-primary bg-primary/5 border-primary/20"
            onClick={e => {
              e.stopPropagation();
              setSelectedLink(row.original);
              setIsStatsDialogOpen(true);
            }}
          >
            {kFormatter(row.original.totalClickCount || 0)}
          </Badge>
        ),
      },
      {
        id: "actions",
        meta: { intrinsic: true },
        cell: ({ row }) => (
          <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
            {row.original.isActive ? (
              <>
                <Tooltip>
                  <Tooltip.Trigger
                    render={
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => {
                          setSelectedLink(row.original);
                          setDialogInitialMode("FORM");
                          setIsSmartLinkDialogOpen(true);
                        }}
                      >
                        <Pencil className="size-4" />
                      </Button>
                    }
                  />
                  <Tooltip.Content>Edit Details</Tooltip.Content>
                </Tooltip>
                <Tooltip>
                  <Tooltip.Trigger
                    render={
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => {
                          setSelectedLink(row.original);
                          setIsStatsDialogOpen(true);
                        }}
                      >
                        <BarChart3 className="size-4" />
                      </Button>
                    }
                  />
                  <Tooltip.Content>View Stats</Tooltip.Content>
                </Tooltip>
              </>
            ) : (
              <Tooltip>
                <Tooltip.Trigger
                  render={
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => handleReactivate(row.original)}
                      className="text-primary hover:text-primary hover:bg-primary/10"
                    >
                      <ArchiveRestore className="size-4" />
                    </Button>
                  }
                />
                <Tooltip.Content>Re-activate</Tooltip.Content>
              </Tooltip>
            )}
          </div>
        ),
      },
    ],
    []
  );

  const renderZeroState = () => (
    <div className="flex flex-col items-center justify-center py-20 px-6 text-center border-2 border-dashed rounded-3xl bg-slate-50/50 mt-4">
      <div className="bg-primary/10 p-5 rounded-full mb-6">
        <Link2 className="size-12 text-primary" />
      </div>
      <h3 className="text-2xl font-bold text-slate-900 mb-3">Maximize Your Impact</h3>
      <p className="text-slate-600 max-w-[540px] mb-8 leading-relaxed text-lg text-center">
        Smart Links are more than just short URLs. They automatically inject high-fidelity UTM tracking into every
        share, allowing you to attribute clicks back to specific social networks, campaigns, and individual players.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10 w-full max-w-[800px]">
        {[
          {
            title: "Dynamic Attribution",
            desc: "Know exactly which network (Instagram vs Facebook) is driving results.",
          },
          {
            title: "Google Analytics",
            desc: "Seamlessly integrates with your existing GA4 dashboards via standard UTMs.",
          },
          { title: "Player Performance", desc: "Track which team members are generating the most engagement." },
        ].map((feat, i) => (
          <div key={i} className="flex flex-col gap-2 p-4 bg-white rounded-xl shadow-sm border border-slate-100">
            <span className="font-bold text-sm text-primary uppercase tracking-wider text-center">{feat.title}</span>
            <p className="text-sm text-slate-500 leading-snug">{feat.desc}</p>
          </div>
        ))}
      </div>
      <Button
        size="lg"
        onClick={() => {
          setSelectedLink(undefined);
          setDialogInitialMode("CREATE_URL_STEP");
          setIsSmartLinkDialogOpen(true);
        }}
        className="h-14 px-8 text-lg"
      >
        <Plus className="size-5 mr-2" />
        Create Your First Smart Link
      </Button>
    </div>
  );

  return (
    <div className="flex flex-col gap-6 py-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or URL..."
              className="pl-9 bg-white"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200 shadow-inner">
            <Button
              variant={statusFilter === "ALL" ? "default" : "ghost"}
              size="sm"
              className={cn(
                "h-8 px-5 rounded-lg transition-all",
                statusFilter === "ALL" ? "shadow-sm" : "text-slate-500 hover:text-slate-900"
              )}
              onClick={() => setStatusFilter("ALL")}
            >
              All
            </Button>
            <Button
              variant={statusFilter === "ACTIVE" ? "default" : "ghost"}
              size="sm"
              className={cn(
                "h-8 px-5 rounded-lg transition-all",
                statusFilter === "ACTIVE" ? "shadow-sm" : "text-slate-500 hover:text-slate-900"
              )}
              onClick={() => setStatusFilter("ACTIVE")}
            >
              Active
            </Button>
            <Button
              variant={statusFilter === "ARCHIVED" ? "default" : "ghost"}
              size="sm"
              className={cn(
                "h-8 px-5 rounded-lg transition-all",
                statusFilter === "ARCHIVED" ? "shadow-sm" : "text-slate-500 hover:text-slate-900"
              )}
              onClick={() => setStatusFilter("ARCHIVED")}
            >
              Archived
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setIsParamsDialogOpen(true)}>
            <Link />
            Manage Tracking
          </Button>
          <Button
            onClick={() => {
              setSelectedLink(undefined);
              setDialogInitialMode("CREATE_URL_STEP");
              setIsSmartLinkDialogOpen(true);
            }}
          >
            <Plus className="size-4" />
            Create Smart Link
          </Button>
        </div>
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Spinner className="size-10" />
        </div>
      ) : data?.content.length === 0 && searchTerm === "" && statusFilter === "ACTIVE" ? (
        renderZeroState()
      ) : (
        <div className="[&_th]:whitespace-nowrap">
          <DataTable
            columns={columns}
            data={data?.content || []}
            pagination={pagination}
            onPaginationChange={setPagination}
            rowCount={data?.pagination.totalCount || 0}
            pageCount={data?.pagination.totalPages || 0}
            onRowClick={row => {
              if (!row.original.isActive) {
                handleReactivate(row.original);
              } else {
                setSelectedLink(row.original);
                setDialogInitialMode("FORM");
                setIsSmartLinkDialogOpen(true);
              }
            }}
            emptyState="No smart links found matching your criteria."
          />
        </div>
      )}

      {isDialogOpen && (
        <SmartLinkDialog
          open={isDialogOpen}
          onOpenChange={setIsSmartLinkDialogOpen}
          smartLink={selectedLink}
          initialMode={dialogInitialMode}
          onSuccess={() => {
            setIsSmartLinkDialogOpen(false);
          }}
        />
      )}

      {isStatsOpen && selectedLink && (
        <SmartLinkStatsDialog open={isStatsOpen} onOpenChange={setIsStatsDialogOpen} smartLink={selectedLink} />
      )}

      {isParamsOpen && <SmartLinkExtraParamsDialog open={isParamsOpen} onOpenChange={setIsParamsDialogOpen} />}
    </div>
  );
}
